import React, { useCallback, useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import * as zod from 'zod';
import moment from 'moment-timezone';
import {
  currencies,
  paymentTriggers,
  paymentTypes,
} from '../../constants/paymentUnits';
import type {
  SpotForm_ChartererFragment,
  SpotForm_ShipownerFragment,
  SpotForm_VesselFragment,
} from '../../graphql';
import {
  Currency,
  PaymentTrigger,
  PaymentType,
  useCreateChartererMutation,
  useCreateShipMutation,
  useCreateShipOwnerMutation,
} from '../../graphql';
import {
  nodesToOptions,
  paymentUnitsToOptions,
  reactSelectStyles,
} from '../../lib/form-util';
import {
  valueOrNull,
  nullableStrToEnum,
  reactSelectOptionSchema,
  stringToNumber,
} from '../../lib/zod-util';
import { useCurrentUser } from '../../state/zustand';
import {
  Box,
  Button,
  DatePicker,
  FormField,
  FormRow,
  FormSection,
  Input,
  Select,
  Text,
} from '../ui';
import { baseContractSchema } from './schema';
import { DEFAULT_LAYCAN_DAYS } from './voyageCharter';
import { DOWNCARET, UPCARET } from '../order/OrderForm';

export const baseContractFieldsSchema = baseContractSchema.extend({
  chartererOrgId: reactSelectOptionSchema.nullable(),
  shipownerOrgId: reactSelectOptionSchema,
  vesselId: reactSelectOptionSchema.nullable(),
  layday: zod.date().nullable(),
  cancelling: zod.date().nullable(),
  paymentTrigger: nullableStrToEnum(PaymentTrigger),
  paymentType: nullableStrToEnum(PaymentType),
  currency: nullableStrToEnum(Currency),
  externalId: zod.string().nullable(),
});

export type BaseContractFieldsSchema = zod.infer<
  typeof baseContractFieldsSchema
>;

interface BaseContractProps<T extends BaseContractFieldsSchema> {
  charterers: SpotForm_ChartererFragment[];
  form: UseFormReturn<T>;
  refetch: () => Promise<unknown>;
  shipowners: SpotForm_ShipownerFragment[];
  timezone?: string;
  vessels: SpotForm_VesselFragment[];
  currencyErrorMessage?: string | null;
}

export default function BaseContractFields<T extends BaseContractFieldsSchema>({
  charterers,
  form,
  refetch,
  shipowners,
  timezone,
  vessels,
  currencyErrorMessage,
}: BaseContractProps<T>): JSX.Element {
  const [isOptionsOpen, setOptionsOpen] = useState(false);
  const organisationId = useCurrentUser()?.organizationId ?? null;

  const {
    control,
    register,
    getValues,
    setValue,
    formState: { errors },
  } = form as UseFormReturn<BaseContractFieldsSchema>; // Cast is required due to issue in react-hook-form (https://github.com/react-hook-form/react-hook-form/issues/6726)

  const chartererOptions = nodesToOptions(charterers);
  const shipownerOptions = nodesToOptions(shipowners);
  const vesselOptions = nodesToOptions(vessels);

  const [
    createCharterer,
    { loading: createChartererLoading, error: createChartererError },
  ] = useCreateChartererMutation();

  const handleCreateCharterer = useCallback(
    async (inputValue: string) => {
      const { data } = await createCharterer({
        variables: {
          input: {
            organization: {
              name: inputValue,
              charterer: true,
              parentOrganizationId: organisationId,
            },
          },
        },
      });

      const { id, name } = { ...data?.createOrganization?.organization };

      if (id) {
        setValue('chartererOrgId', { value: id, label: name ?? '' });
      } else {
        console.error('No id received for created charterer');
        toast.error('Error selecting created charterer, please reload page');
      }

      await refetch();
    },
    [createCharterer, refetch, setValue, organisationId],
  );

  const [
    createShipOwner,
    { loading: createShipOwnerLoading, error: createShipOwnerError },
  ] = useCreateShipOwnerMutation();

  const handleCreateShipOwner = useCallback(
    async (inputValue: string) => {
      const { data } = await createShipOwner({
        variables: {
          input: {
            organization: {
              name: inputValue,
              shipOwner: true,
              parentOrganizationId: organisationId,
            },
          },
        },
      });

      const { id, name } = { ...data?.createOrganization?.organization };

      if (id) {
        setValue('shipownerOrgId', { value: id, label: name ?? '' });
        setValue('vesselId', null);
      } else {
        console.error('No id received for created shipowner');
        toast.error('Error selecting created charterer, please reload page');
      }

      await refetch();
    },
    [createShipOwner, refetch, setValue, organisationId],
  );

  const [
    createVessel,
    { loading: createVesselLoading, error: createVesselError },
  ] = useCreateShipMutation();

  const handleCreateVessel = useCallback(
    async (inputValue: string) => {
      const shipownerOrgId = getValues('shipownerOrgId')?.value;

      if (!shipownerOrgId) {
        toast.error('Cannot create vessel with no shipowner selected');
        return;
      }

      const { data } = await createVessel({
        variables: {
          input: {
            ship: {
              name: inputValue,
              organizationId: shipownerOrgId,
            },
          },
        },
      });

      const { id, name } = { ...data?.createShip?.ship };

      if (id) {
        setValue('vesselId', { value: id, label: name ?? '' });
      } else {
        console.error('No id received for created shipowner');
        toast.error('Error selecting created vessel, please reload page');
      }

      await refetch();
    },
    [createVessel, refetch, getValues, setValue],
  );

  const onLaycanChange = (changedField: 'layday' | 'cancelling') => {
    if (!timezone) return;

    const { layday, cancelling } = getValues();

    if (changedField === 'layday' && layday) {
      setValue(
        'cancelling',
        moment(layday)
          .tz(timezone)
          .add(DEFAULT_LAYCAN_DAYS - 1, 'days')
          .endOf('day')
          .toDate(),
      );
    }

    if (
      changedField === 'cancelling' &&
      cancelling &&
      (!layday || !moment(cancelling).isAfter(moment(layday)))
    ) {
      setValue(
        'layday',
        moment(cancelling)
          .tz(timezone)
          .subtract(DEFAULT_LAYCAN_DAYS - 1, 'days')
          .startOf('day')
          .toDate(),
      );
    }
  };

  useEffect(() => {
    if (createChartererError) {
      toast.error(`Error creating charterer: ${createChartererError.message}`);
    }
    if (createShipOwnerError) {
      toast.error(`Error creating shipowner: ${createShipOwnerError.message}`);
    }
    if (createVesselError) {
      toast.error(`Error creating vessel: ${createVesselError.message}`);
    }
  }, [createChartererError, createShipOwnerError, createVesselError]);

  return (
    <FormSection title="Contract basics">
      <FormRow>
        <FormField
          label="Charterer"
          data-testid="cargo-form-contract-basics-charterer"
          width={200}
          hasError={!!errors.chartererOrgId || !!createChartererError}
        >
          <Controller
            name="chartererOrgId"
            control={control}
            render={({ field }) => (
              <CreatableSelect
                isLoading={createChartererLoading}
                isDisabled={createChartererLoading}
                styles={reactSelectStyles}
                options={chartererOptions}
                onCreateOption={handleCreateCharterer}
                {...field}
              />
            )}
          />
        </FormField>
        <FormField
          label="Shipowner"
          data-testid="cargo-form-contract-basics-shipowner"
          width={200}
          hasError={!!errors.shipownerOrgId || !!createShipOwnerError}
        >
          <Controller
            name="shipownerOrgId"
            control={control}
            render={({ field }) => (
              <CreatableSelect
                isDisabled={createShipOwnerLoading || createChartererLoading}
                isLoading={createShipOwnerLoading}
                styles={reactSelectStyles}
                options={shipownerOptions}
                onCreateOption={handleCreateShipOwner}
                {...field}
                onChange={(selected) => {
                  setValue('vesselId', null);
                  setValue(
                    'shipownerOrgId',
                    selected
                      ? {
                          value: selected.value ?? null,
                          label: selected.label ?? '',
                        }
                      : null,
                  );
                }}
              />
            )}
          />
        </FormField>
        <FormField
          label="Vessel"
          data-testid="cargo-form-contract-basics-vessel"
          width={200}
          hasError={!!errors.vesselId || !!createVesselError}
        >
          <Controller
            name="vesselId"
            control={control}
            render={({ field }) => (
              <CreatableSelect
                isDisabled={createVesselLoading}
                isLoading={createVesselLoading}
                styles={reactSelectStyles}
                options={vesselOptions}
                onCreateOption={handleCreateVessel}
                {...field}
              />
            )}
          />
        </FormField>
        <FormField
          label="Layday"
          data-testid="cargo-form-contract-basics-layday"
          width={200}
          helpText={timezone}
        >
          <Controller
            defaultValue={null}
            control={control}
            name="layday"
            render={({ field: { value, onChange } }) => (
              <DatePicker
                showTime
                showTimeInput
                onChange={(newValue) => {
                  onChange(newValue);
                  onLaycanChange('layday');
                }}
                selected={value}
                defaultTimePickerType="START_OF_DAY"
                timeZone={timezone}
              />
            )}
          />
        </FormField>
        <FormField
          label="Cancelling"
          data-testid="cargo-form-contract-basics-cancelling"
          width={200}
          helpText={timezone}
        >
          <Controller
            defaultValue={null}
            control={control}
            name="cancelling"
            render={({ field: { value, onChange } }) => (
              <DatePicker
                showTime
                showTimeInput
                onChange={(newValue) => {
                  onChange(newValue);
                  onLaycanChange('cancelling');
                }}
                selected={value}
                defaultTimePickerType="END_OF_DAY"
                timeZone={timezone}
              />
            )}
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField
          label="Contract currency"
          data-testid="cargo-form-contract-basics-contract-currency"
          width={200}
          hasError={!!errors.currency}
        >
          <Select
            {...register('currency', {
              setValueAs: valueOrNull,
            })}
            options={paymentUnitsToOptions(currencies)}
            placeholder="-"
            enablePlaceholder
          />
          {currencyErrorMessage && (
            <Text mt={2} color="red">
              {currencyErrorMessage}
            </Text>
          )}
        </FormField>
        <FormField
          label="Contract name"
          data-testid="cargo-form-contract-basics-name"
          width={200}
        >
          <Input {...register('name')} type="text" />
        </FormField>

        <FormField
          label="Voyage external ID"
          data-testid="cargo-form-contract-basics-voyage-external-ID"
          width={200}
        >
          <Input disabled {...register('externalId')} type="text" />
        </FormField>

        <FormField
          label="Contract Freight (Total)"
          data-testid="cargo-form-contract-basics-freight-total"
          width={200}
        >
          <Input
            {...register('lumpsum', { setValueAs: stringToNumber })}
            type="number"
          />
        </FormField>
      </FormRow>
      <FormRow>
        <Button
          onPress={() => setOptionsOpen(!isOptionsOpen)}
          variant="secondary"
          mr={2}
        >
          Contract options
          {isOptionsOpen ? UPCARET : DOWNCARET}
        </Button>
      </FormRow>
      <Box
        maxWidth={870}
        borderWidth="1px"
        borderStyle="solid"
        borderColor="input.border"
        backgroundColor="#F2F4F6"
        padding={4}
        marginTop={3}
        display={isOptionsOpen ? undefined : 'none'}
      >
        <FormRow>
          <FormField label="Demurrage" width={200}>
            <Input
              {...register('demurrage', { setValueAs: stringToNumber })}
              type="number"
            />
          </FormField>
          <FormField label="Payment days" width={200}>
            <Input
              {...register('paymentTime', { setValueAs: stringToNumber })}
              type="number"
            />
          </FormField>
          <FormField
            label="Payment trigger"
            width={200}
            hasError={!!errors.paymentTrigger}
          >
            <Select
              {...register('paymentTrigger', {
                setValueAs: valueOrNull,
              })}
              options={paymentUnitsToOptions(paymentTriggers)}
              placeholder="-"
              enablePlaceholder
            />
          </FormField>
          <FormField
            label="Payment terms"
            width={200}
            hasError={!!errors.paymentType}
          >
            <Select
              {...register('paymentType', {
                setValueAs: valueOrNull,
              })}
              options={paymentUnitsToOptions(paymentTypes)}
              placeholder="-"
              enablePlaceholder
            />
          </FormField>
        </FormRow>
      </Box>
    </FormSection>
  );
}
